import { lazy, Suspense } from "react";
import { useRoutes } from "react-router-dom";
import MainRoutes from "./main-routes";
import LandingPage from "../pages";
import Spinner from "../components/spinner";
import MarketingLayout from "../components/landing2/MarketingLayout";
import AuthGuard from "../utils/route-guard/auth-guard";

// Landing (LandingPage / Landing) stays eager so the marketing page renders
// instantly. Every other route is code-split, so visiting "/" no longer
// downloads the whole app (MUI/antd/rsuite/dashboard) up front.
const Signup = lazy(() => import("../pages/auth/signup"));
const Verify = lazy(() => import("../pages/auth-test/email-verification"));
const Signin = lazy(() => import("../pages/auth-test/signin"));
const ForgotPassword = lazy(() => import("../pages/auth-test/forget-password"));
const ResetPassword = lazy(() => import("../pages/auth-test/reset-password"));
const Pricing = lazy(() => import("../pages/pricing"));
const AuthorizePayment = lazy(() => import("../pages/authorize-payment"));
const ConfirmOrder = lazy(
  () => import("../pages/authorize-payment/confirm-order"),
);
const ReleaseFund = lazy(() => import("../pages/release-fund"));
const ReportOrder = lazy(
  () => import("../pages/authorize-payment/report-order"),
);
const BuyerRefund = lazy(() => import("../pages/refund/buyer-refund"));
const GeneratePayment = lazy(
  () => import("../pages/dashboard/payment-dashboard/verify-payment"),
);
const ConfirmBuyerPayment = lazy(
  () => import("../pages/generate-payment/confirm-payment"),
);
const PaymentLink = lazy(() => import("../pages/generate-payment"));
const About = lazy(() => import("../components/landing2/AboutPage"));
const Works = lazy(() => import("../pages/how-it-works"));
const Privacy = lazy(() => import("../components/landing2/PrivacyPage"));
const FAQ = lazy(() => import("../components/landing2/FaqPage"));
const Error404 = lazy(() => import("../pages/404"));
const Terms = lazy(() => import("../components/landing2/TermsPage"));
const ContactUs = lazy(() => import("../components/landing2/ContactPage"));
const Sales = lazy(() => import("../components/landing2/SalesPage"));
const CreateOrder = lazy(() => import("../pages/create-order"));
const SocialConfirmation = lazy(
  () => import("../pages/settings/kyc/social-confirmation"),
);
const Mediation = lazy(() => import("../pages/mediation-center/Mediation"));
const Products = lazy(() => import("../components/product/Products"));
const QuickPay = lazy(() => import("../pages/quickpay"));
const Openpay = lazy(() => import("../pages/openpay"));
const SignupPrompt = lazy(() => import("../pages/sign-up-prompt"));
const Storefront = lazy(() => import("../pages/storefront"));
const StorefrontProductDetails = lazy(
  () => import("../pages/storefront/product-details"),
);
const DeleteAccount = lazy(() => import("../pages/delete-account"));

// ================|| ROUTING RENDER ||================ //

export default function ThemeRoutes() {
  const element = useRoutes([
    // Public marketing pages share one shell (Navbar/Footer mount once and
    // persist across navigations) via MarketingLayout's <Outlet/>.
    {
      element: <MarketingLayout />,
      children: [
        {
          index: true,
          element: (
            <AuthGuard>
              <LandingPage />
            </AuthGuard>
          ),
        },
        { path: "vendors", element: <Sales /> },
        // Reuse the landing page itself — pre-selects the matching tab in the
        // roles section and scrolls there (see VendstashLanding/app.tsx).
        { path: "for-buyers", element: <LandingPage /> },
        { path: "for-vendors", element: <LandingPage /> },
        { path: "about", element: <About /> },
        { path: "privacy-policy", element: <Privacy /> },
        { path: "terms", element: <Terms /> },
        { path: "faq", element: <FAQ /> },
        { path: "contact", element: <ContactUs /> },
      ],
    },
    ...MainRoutes,
    { path: "/signup", element: <Signup /> },
    { path: "/signup/verify", element: <Verify /> },
    { path: "/signin", element: <Signin /> },
    { path: "/pricing", element: <Pricing /> },
    { path: "/forgot-password", element: <ForgotPassword /> },
    { path: "/reset-password", element: <ResetPassword /> },
    { path: "/create-order", element: <CreateOrder /> },
    { path: "/authorize-payment/:id", element: <AuthorizePayment /> },
    { path: "/confirm-payment/:id", element: <ConfirmBuyerPayment /> },
    { path: "/generate-payment/:id", element: <GeneratePayment /> },
    { path: "/release-fund/:orderId", element: <ReleaseFund /> },
    { path: "/confirm-order/:id", element: <ConfirmOrder /> },
    // { path: "/upload-proof/:orderId", element: <RequestPayment /> },
    { path: "/user/report-order", element: <ReportOrder /> },
    { path: "/buyer-refund", element: <BuyerRefund /> },
    { path: "/payment-link/:id", element: <PaymentLink /> },
    { path: "/how-it-works", element: <Works /> },
    { path: "/products", element: <Products /> },
    { path: "/mediation", element: <Mediation /> },
    // Public Storefront route (buyer view)
    {
      path: "/store/:id/product/:productId",
      element: <StorefrontProductDetails />,
    },

    { path: "/store/:id", element: <Storefront /> },
    { path: "/*", element: <Error404 /> },
    { path: "social-confirmation", element: <SocialConfirmation /> },
    { path: "/quickpay/:linkName", element: <QuickPay /> },
    { path: "/openpay/:linkName", element: <Openpay /> },
    { path: "/signup-prompt", element: <SignupPrompt /> },
    { path: "/delete-account", element: <DeleteAccount /> },
  ]);

  return (
    <Suspense
      fallback={
        <div className='flex h-[60vh] w-full items-center justify-center'>
          <Spinner size='md' />
        </div>
      }>
      {element}
    </Suspense>
  );
}
